/**
 * failedAckStore — which failed queue entries has the rep already acknowledged?
 *
 * The OfflineIndicator's dismissal was `useState(0)`: a COUNT, held in memory
 * (BUG-2026-07-14-VC559). Two consequences, both lived:
 *
 *  1. It forgot on every reload. The operator dismissed the banner; the app reloaded;
 *     the full-width red overlay came straight back over the header controls. From
 *     the rep's side the × simply did not work — "I cannot remove it, it BLOCKS
 *     everything".
 *  2. A count cannot tell "the same stuck report I already dismissed" from "a new
 *     failure I have never seen". Acknowledgement is about IDENTITY, so it is stored
 *     as a SET OF ENTRY IDS.
 *
 * Persisted to localStorage (this path is storage-fence allow-listed). Stale ids are
 * pruned against the live queue so the set cannot grow without bound.
 */

const ACK_KEY = 'offline:ackedFailedIds'

/**
 * Read the acknowledged-id set.
 *
 * A malformed/absent value yields an EMPTY set — the loud, safe direction: we
 * re-raise a banner we might have already shown, rather than suppress one we have
 * not. Never the reverse (D-33 R2: degrade toward loud).
 */
export function getAckedFailedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(ACK_KEY)
    if (raw === null) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch (err) {
    console.warn('[offlineAck] could not read the acknowledged-failure set; treating it as empty', err)
    return new Set()
  }
}

/**
 * Acknowledge exactly `ids` (the failures currently on screen), dropping any
 * previously-acked id that is no longer among them — that entry has left the queue,
 * so remembering it forever would leak.
 */
export function ackFailedIds(ids: readonly string[]): void {
  try {
    localStorage.setItem(ACK_KEY, JSON.stringify([...new Set(ids)]))
  } catch (err) {
    console.error('[offlineAck] could not persist the acknowledged-failure set — the banner may re-raise on reload', err)
  }
}

/**
 * True when `failedIds` contains at least one entry the rep has NOT acknowledged.
 * That — and only that — earns the loud banner; everything else stays a quiet,
 * non-occluding badge.
 */
export function hasUnackedFailure(failedIds: readonly string[]): boolean {
  const acked = getAckedFailedIds()
  return failedIds.some(id => !acked.has(id))
}

/**
 * Forget every acknowledgement — so the next failure raises the loud banner again.
 *
 * Used on logout / cache-clear (a new rep on the same device must not inherit the
 * previous one's dismissals), and by tests. Storage access is confined to this module
 * because this path is the storage-fence boundary; callers never touch localStorage.
 */
export function clearAckedFailedIds(): void {
  try {
    localStorage.removeItem(ACK_KEY)
  } catch (err) {
    console.error('[offlineAck] could not clear the acknowledged-failure set', err)
  }
}
